import React from 'react'
import { Card, CardContent } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import type { KeyStatSurveyItem } from '../../../../models/Report'
import { SurveyQuestionType } from '../../../../models/Survey'
import { KeyStatType } from '../constants'
import { blue } from '../../../../colors'
import { NOT_APPLICABLE } from '../../../SurveyTempleteFeature/components/QuestionsCreation/CreateMultipleChoiceQuestionCard'
import { formatValue, yAxisLabelFormatter, getFilteredLinearScaleOptions } from '../helper'

interface LineChartProps {
  titleComponent: React.ReactNode
  report: KeyStatSurveyItem
  titles?: string[]
  selectedStat?: KeyStatType
}

const LineChart: React.FC<LineChartProps> = ({
  titleComponent,
  report,
  titles = [],
  selectedStat
}) => {
  const questionType = report.itemType

  const isTopOrBottom2 = selectedStat === KeyStatType.TOP_2 || selectedStat === KeyStatType.BOTTOM_2

  // For LinearScale + AVG, get min/max weights and build a mapping
  const isAvg = selectedStat === KeyStatType.AVERAGE

  const getChartData = (): any[] => {
    if (!selectedStat) return []
    const statCalc = report.calculations?.find((calc: any) => calc.name === selectedStat)
    // Only multiply by 100 for TOP_2/BOTTOM_2
    if (statCalc && isTopOrBottom2 && Array.isArray(statCalc.values)) {
      return statCalc.values.map((v: any) => typeof v === 'number' ? v * 100 : v)
    } else if (statCalc && Array.isArray(statCalc?.values)) {
      return statCalc.values
    }
    return []
  }

  const chartData = getChartData()

  // Helper to build yAxis config for chart
  const getYAxisConfig = (): any => {
    if (isAvg && questionType === SurveyQuestionType.LinearScale) {
      // Defensive: ensure options is a non-empty array and values are numbers
      let linearScaleMin = 1
      let linearScaleMax = 5
      if (Array.isArray(report.options) && report.options?.length > 0) {
        // Try .value, fallback to .name if .value does not exist
        const getVal = (opt: any): number => typeof opt.value !== 'undefined' ? Number(opt.value) : Number(opt.name)
        const minVal = getVal(report.options[0])
        const maxVal = getVal(report.options[report.options.length - 1])
        if (!isNaN(minVal)) linearScaleMin = minVal
        if (!isNaN(maxVal)) linearScaleMax = maxVal
      }
      return {
        type: 'value',
        min: linearScaleMin,
        max: linearScaleMax,
        axisLine: { show: false },
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
          margin: 10,
          width: 400,
          overflow: 'truncate',
          formatter: (value: number | string) => Number(value)
        },
        axisTick: { show: false },
        splitLine: { show: true },
        nameGap: 40,
        nameRotate: 0
      }
    }
    if (isTopOrBottom2) {
      return {
        type: 'value',
        min: 0,
        max: 100,
        axisLine: { show: false },
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
          overflow: 'truncate',
          formatter: (v: number | string) => yAxisLabelFormatter(v, isTopOrBottom2, questionType, report)
        },
        axisTick: { show: false },
        splitLine: { show: true },
        nameGap: 40,
        nameRotate: 0
      }
    }
    return {
      type: 'category',
      data: report.options?.map((opt: any) => opt.name) || [],
      axisLine: { show: false },
      axisLabel: {
        fontSize: 14,
        fontWeight: 'bold',
        margin: 10,
        width: 400,
        overflow: 'break',
        formatter: (v: number | string) => yAxisLabelFormatter(v, isTopOrBottom2, questionType, report)
      },
      axisTick: { show: false },
      splitLine: { show: true },
      nameGap: 40,
      nameRotate: 0
    }
  }

  const option = {
    grid: { left: 180 },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        if (Array.isArray(params) && params.length > 0) {
          const axisLabel = params[0].axisValueLabel || params[0].name || ''
          const lines = [axisLabel]
          params.forEach((item: any) => {
            const marker = String(item.marker ?? '')
            const value = item.value
            if (value === undefined) return
            if (isTopOrBottom2) {
              lines.push(`${marker}${formatValue(value)}%`)
            } else {
              lines.push(`${marker}${formatValue(value)}`)
            }
          })
          return lines.join('<br/>')
        }
        return ''
      }
    },
    xAxis: {
      type: 'category',
      data: titles,
      nameLocation: 'middle',
      nameGap: 30,
      axisLine: { show: false },
      axisLabel: { fontWeight: 'bold', margin: 20 },
      axisTick: { show: false }
    },
    yAxis: getYAxisConfig(),
    series: [
      {
        data: chartData,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 14,
        lineStyle: {
          width: 3,
          color: blue[600]
        }
      }
    ]
  }

  return (
    <Card>
      <CardContent>
        {titleComponent}
        <ReactECharts
          lazyUpdate
          style={{ width: '100%', height: '400px', maxHeight: 500 }}
          option={option}
        />
      </CardContent>
    </Card>
  )
}

export default LineChart
